import {useEffect, useState} from 'react'
import {useHeaderContext} from '../../../../contexts/header-context'

/* Distance (px) we want absolutely-positioned tile controls (tooltip,
 * video play/pause, etc.) to sit BELOW the header's visible bottom when
 * the header is transparent and the tile lives behind it. Matches the
 * `lg` baseline already used by the tooltip control. */
const CLEARANCE_PX = 24

/**
 * Compute an effective top-offset (px) for an absolutely-positioned control
 * anchored inside a tile that may render behind a transparent (overlay)
 * header.
 *
 * The decision is made purely from geometry, NOT from the header variant:
 *  - Transparent header → `position: absolute, top: 0` so it does NOT take
 *    layout space; tiles below render at document-top 0 → measured
 *    documentTop is small → we push the control down by `headerHeight + 24`.
 *  - Opaque header → in normal flow, takes layout space; tiles render below
 *    it → documentTop ≥ headerHeight → the computed offset is ≤ 24 and we
 *    return `null` so the authored default applies.
 *
 * Going purely by geometry also handles cases where transparency is decided
 * by CMS `HeaderVariations` rather than the env-var-driven context variant
 * (`useTransparentHeader` resolves an effective variant that the context
 * doesn't expose).
 *
 * `tileRef` should point to the nearest positioning ancestor that contains
 * the control (otherwise `getBoundingClientRect` returns the wrong element's
 * coordinates and the math is off).
 *
 * Returns `null` when no override is needed; callers should fall back to
 * their authored default in that case.
 */
export const useHeaderClearance = (tileRef) => {
    const {headerHeight} = useHeaderContext() || {}
    const [topPx, setTopPx] = useState(null)

    useEffect(() => {
        if (!tileRef?.current || typeof window === 'undefined') {
            setTopPx(null)
            return undefined
        }

        const compute = () => {
            const node = tileRef.current
            if (!node || !headerHeight) {
                setTopPx(null)
                return
            }
            const rect = node.getBoundingClientRect()
            /* Detect transparent header purely from geometry: when the
             * header is in normal flow (opaque), every tile below it
             * starts at `documentTop >= headerHeight`; when the header
             * is `position: absolute` (transparent overlay), tiles
             * placed at the top of the page render at `documentTop ≈ 0`
             * and end up behind the header.
             *
             * We only override the authored offset on tiles that are
             * actually behind the header — anything further down the
             * page already clears it on its own. */
            const documentTop = rect.top + window.scrollY
            if (documentTop >= headerHeight) {
                setTopPx(null)
                return
            }
            /* The override value itself is intentionally simple: header
             * height plus a 24px gutter. Applied as the control's `top`
             * inside an absolutely-positioned wrapper whose nearest
             * positioning ancestor is the tile (which itself sits at
             * page top), so this lands the control 24px below the
             * header's visible bottom edge. */
            setTopPx(headerHeight + CLEARANCE_PX)
        }

        /* Schedule the first measurement on the next frame so layout has
         * had a chance to settle (the carousel parent flips between Grid
         * and Carousel modes via CSS, and the video block swaps responsive
         * sources on mount). */
        const raf = window.requestAnimationFrame(compute)
        window.addEventListener('resize', compute)

        /* Re-measure when the tile itself, or layout above it, changes
         * size. Observing `document.body` catches lazy-loaded media above
         * the tile pushing it down; observing the tile catches its own
         * intrinsic-size changes (e.g. responsive media switches). */
        let ro
        if (typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(compute)
            ro.observe(tileRef.current)
            if (document.body) ro.observe(document.body)
        }

        return () => {
            window.cancelAnimationFrame(raf)
            window.removeEventListener('resize', compute)
            ro?.disconnect()
        }
    }, [headerHeight, tileRef])

    return topPx
}
